import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useAuthStore } from "@/stores/auth-store";
import { slackApi } from "@/lib/api/slack";
import { ApiError } from "@/lib/api/client";
import { rememberPostLoginReturnTo } from "@/lib/post-login-return-to";
import { usePageTitle } from "@/hooks/use-page-title";
import { BrandMark } from "@/components/brand-mark";

type LinkState =
  | { kind: "pending" }
  | { kind: "linked"; slackTeamName: string }
  | { kind: "failed"; message: string };

/**
 * Where the one-time link from Slack lands (ENG-411). A signed-out visitor is
 * sent to sign in and brought back here; a signed-in one has the token
 * redeemed for their account, which links their Slack identity to CompBox.
 */
export function SlackLinkPage() {
  usePageTitle("Link Slack");
  const { isAuthenticated, isLoading } = useAuthStore();
  const location = useLocation();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState<LinkState>({ kind: "pending" });
  const started = useRef(false);

  useEffect(() => {
    if (isLoading || !isAuthenticated || started.current) return;
    started.current = true;
    if (!token) {
      setState({ kind: "failed", message: "This link is missing its token. Run /compbox in Slack to get a new one." });
      return;
    }
    slackApi
      .confirmLink(token)
      .then((res) => {
        setState({ kind: "linked", slackTeamName: res.data.slackTeamName });
      })
      .catch((err: unknown) => {
        setState({
          kind: "failed",
          message: err instanceof ApiError ? err.message : "Couldn't link your Slack account. Please try again.",
        });
      });
  }, [isLoading, isAuthenticated, token]);

  if (!isLoading && !isAuthenticated) {
    rememberPostLoginReturnTo(`${location.pathname}${location.search}`);
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="flex min-h-screen flex-col items-center bg-background px-4 py-10">
      <BrandMark />
      <div className="mt-10 w-full max-w-md rounded-lg border border-border bg-card p-8 text-center">
        {state.kind === "pending" && (
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden />
            <p className="text-sm text-muted-foreground">Linking your Slack account…</p>
          </div>
        )}
        {state.kind === "linked" && (
          <div className="space-y-4">
            <CheckCircle2 className="mx-auto h-10 w-10 text-green-600" aria-hidden />
            <h1 className="text-xl font-semibold text-foreground">Slack linked</h1>
            <p className="text-sm text-muted-foreground">
              Your Slack account in {state.slackTeamName} is now linked to CompBox. You can head back to Slack.
            </p>
            <Link to="/dashboard" className="block text-sm text-primary hover:underline">
              Go to dashboard
            </Link>
          </div>
        )}
        {state.kind === "failed" && (
          <div className="space-y-4">
            <XCircle className="mx-auto h-10 w-10 text-destructive" aria-hidden />
            <h1 className="text-xl font-semibold text-foreground">Couldn&apos;t link Slack</h1>
            <p className="text-sm text-muted-foreground" role="alert">
              {state.message}
            </p>
            <Link to="/dashboard" className="block text-sm text-primary hover:underline">
              Go to dashboard
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
